import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toStarterDraft, toStarterDrafts, type StarterDraft } from '../settings-draft';
import { STARTER_QUESTION_MAX_LENGTH } from '../types';
import { GHOST_FOCUS_RING } from '../ui-chrome';
import { SettingsFieldCard } from './settings-field-card';

export const STARTER_PLACEHOLDER = 'e.g. Help me pick a coffee';

interface StarterQuestionsSettingsProps {
	starters: StarterDraft[];
	onChange: (starters: StarterDraft[]) => void;
}

/**
 * The conversation starters: questions the live chat shows as tappable pills when a chat starts
 * (Figma Sales-Assistant 2025:5413). The brand writes them, as many as it likes; a tapped one is
 * sent as the shopper's first message. Edits stay in the tab's draft until Save.
 */
export const StarterQuestionsSettings = ({ starters, onChange }: StarterQuestionsSettingsProps) => {
	const setText = (id: string, text: string) =>
		onChange(starters.map((starter) => (starter.id === id ? { ...starter, text } : starter)));

	const remove = (id: string) => {
		const next = starters.filter((starter) => starter.id !== id);
		onChange(next.length ? next : toStarterDrafts([]));
	};

	// One empty field at a time: a new one is offered once every field has text.
	const canAdd = starters.every((starter) => starter.text.trim() !== '');

	return (
		<SettingsFieldCard
			title='Conversation starters'
			label='Questions shown when a chat starts'
			infoAlt='About conversation starters'
			info='Shoppers tap one to start the chat; it is sent as their first message.'
		>
			{starters.map((starter, index) => (
				<div key={starter.id} className='flex w-full items-center gap-2'>
					<Input
						aria-label={`Starter question ${index + 1}`}
						value={starter.text}
						maxLength={STARTER_QUESTION_MAX_LENGTH}
						placeholder={STARTER_PLACEHOLDER}
						onChange={(event) => setText(starter.id, event.target.value)}
						className='h-10 flex-1 rounded-2xl border-gray-500 py-0 text-xs leading-4 text-ink placeholder:text-gray-800'
					/>
					<Button
						type='button'
						variant='ghost'
						size='icon-sm'
						aria-label={`Remove starter question ${index + 1}`}
						onClick={() => remove(starter.id)}
						className={cn('size-6 rounded-[4px] text-gray-900 hover:bg-gray-100 hover:text-gray-900', GHOST_FOCUS_RING)}
					>
						<X className='size-3.5' />
					</Button>
				</div>
			))}
			{canAdd && (
				<Button
					type='button'
					variant='outline'
					onClick={() => onChange([...starters, toStarterDraft('')])}
					className='h-8 w-fit gap-1.5 border-gray-500 bg-white px-3 text-[13px] font-normal text-ink shadow-none hover:bg-gray-50'
				>
					<Plus className='size-3.5' /> Add question
				</Button>
			)}
		</SettingsFieldCard>
	);
};
